// Global Instructions Rule Applied!
//
// LPs grid (FUND MANAGEMENT → LPs) on the canonical TableView: columns from
// cfg.reporting_columns (LP link, Team name, Team name set, LP status, Funds,
// Closed commitments, Owners). Two custom cells: the LP name opens the
// account form (the recordLink button — design patterns), and any column the
// cfg marks editable (Team name, owner 2026-09-14) renders the kit's inline
// InputManager cell — save-on-click-away, PATCH /accounts through the field's
// own auto-patch, the loaded row patched on success — the Companies grid
// pattern, reduced to the plain text field this grid edits.
import React, { useEffect, useMemo, useRef } from 'react'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import type { EditingContext } from '../../../ui_configs/table_grid.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type TextFieldConfig } from '../../../core/components/InputManager/types.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import type { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'
import { LpRow, LpRowFields } from '../../../state_manager/data_models/lps_model.ts'
import type { FilterOptions, FilterConfig } from '../../../core/components/TableView/TableViewTypes.ts'

type GridRow = LpRow & { id: string; key: string }

interface LpsTableProps {
  data: LpRow[]
  loading: boolean
  loadingMore?: boolean
  hasMore?: boolean
  currentPage?: number
  gridColumns: ViewConfigColumn[]
  activeFilters?: FilterValue[]
  activeSorting?: SortConfig[]
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  onLoadMore?: (page: number, pageSize: number) => void | Promise<void>
  onFiltersChange?: (filters: FilterConfig) => void
  onSortChange?: (sortConfig: any) => void
  /** An inline cell was saved (the field's auto-patch already wrote it). */
  onCellSaved: (accountId: string, updates: Record<string, unknown>) => void
}

export const LpsTable = React.memo(function LpsTable({
  data,
  loading,
  loadingMore = false,
  hasMore = false,
  currentPage = 1,
  gridColumns,
  activeFilters = [],
  activeSorting = [],
  filterOptions = {},
  filterOptionsLoading = false,
  onLoadMore,
  onFiltersChange,
  onSortChange,
  onCellSaved
}: LpsTableProps) {
  const { openFormInTab } = useTabManagerContext()

  // The kit calls this for every column whose cfg says editable. The field
  // key is table__column, so the auto-patch derives PATCH /accounts
  // {id, team_name} from it; the id is the row's account.
  const renderInputManagerColumn = (
    record: GridRow,
    columnKey: string,
    _fieldId: string,
    fieldType: FieldTypeEnum,
    editingContext: EditingContext,
    columnInfo: { title?: string; editable?: boolean } = {}
  ) => {
    // Only the plain text field is editable here (Team name); anything else
    // the cfg marks editable renders read-only until this grid supports it.
    const raw = (record as Record<string, unknown>)[columnKey]
    if (fieldType !== FieldTypeEnum.TEXT) return typeof raw === 'string' ? raw : null
    const field: TextFieldConfig = {
      key: columnKey,
      label: columnInfo.title ?? columnKey,
      type: FieldTypeEnum.TEXT,
      value: typeof raw === 'string' ? raw : '',
      editable: columnInfo.editable !== false,
      placeholder: '—'
    }
    return (
      <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <InputManager
          inputMode='read'
          labelPosition='none'
          fields={[field]}
          onChange={(_key, value) => editingContext?.onCellDataChange?.(value)}
          onSave={() => {}} // Auto-patch managed
          onPatchSuccess={(patchedId, updates) => onCellSaved(String(patchedId), updates)}
          onFieldEdit={(isEditing) => editingContext?.onFieldEdit?.(columnKey, isEditing)}
          enableAutoPatch={true}
          patchId={record.accounts__id}
          recordId={record.accounts__id}
          className='cell-edit-input-manager'
          textClassName='truncate'
          currentRecord={record}
        />
      </div>
    )
  }
  const renderInputManagerColumnRef = useRef(renderInputManagerColumn)
  useEffect(() => {
    renderInputManagerColumnRef.current = renderInputManagerColumn
  })

  const { columns: baseColumns, frozenColumns } = useMemo(
    () => createTableColumns({ columns: gridColumns, fallbackRecordModuleKey: 'lps', renderInputManagerColumnRef }),
    [gridColumns]
  )

  const columns = useMemo(
    () =>
      baseColumns.map((col) => {
        if (String(col.key) !== LpRowFields.accounts__name) return col
        return {
          ...col,
          render: (value: unknown, record: GridRow) => (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab('accounts', record.accounts__id, typeof value === 'string' ? value : undefined)}
            >
              {typeof value === 'string' && value ? value : record.accounts__name}
            </button>
          )
        }
      }),
    [baseColumns, openFormInTab]
  )

  const dataSource = useMemo<GridRow[]>(
    () => (data ?? []).map((row) => ({ ...row, id: row.accounts__id, key: row.accounts__id })),
    [data]
  )

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (columns.length === 0) return null

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView<GridRow>
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        onLoadMore={onLoadMore}
        currentFilters={currentFilters}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={currentSortConfig}
        frozenColumns={frozenColumns}
        headerStyle={{ padding: '5px', minHeight: 40, backgroundColor: '#f5f5f5' }}
      />
    </div>
  )
})

export default LpsTable
